import { LETTERS, PitchClass, mod, parsePitchClass, pcName, pcNumber, spellOnLetter } from './pitch';

export type Mode = 'major' | 'minor';

export interface Key {
  tonic: PitchClass;
  mode: Mode;
}

const MAJOR = [0, 2, 4, 5, 7, 9, 11];
const NATURAL_MINOR = [0, 2, 3, 5, 7, 8, 10];

/** "C" → C major, "d" → D minor, "F#" → F♯ major, "bb" → B♭ minor. */
export function parseKey(token: string): Key {
  const t = token.trim().replace(/:$/, '');
  const minor = t[0] === t[0].toLowerCase();
  return { tonic: parsePitchClass(t[0].toUpperCase() + t.slice(1)), mode: minor ? 'minor' : 'major' };
}

export function keyToken(k: Key): string {
  const n = pcName(k.tonic, false);
  return k.mode === 'minor' ? n[0].toLowerCase() + n.slice(1) : n;
}

export function keyName(k: Key): string {
  return `${pcName(k.tonic)} ${k.mode}`;
}

export function sameKey(a: Key, b: Key): boolean {
  return a.mode === b.mode && a.tonic.letter === b.tonic.letter && a.tonic.alter === b.tonic.alter;
}

/**
 * Pitch class of a scale degree (1–7).
 * In minor, `raised` selects the raised 6th/7th of the melodic/harmonic forms.
 */
export function degreePc(key: Key, degree: number, raised = false): PitchClass {
  const d = mod(degree - 1, 7);
  const semis = key.mode === 'major' ? MAJOR[d] : NATURAL_MINOR[d] + (raised && (d === 5 || d === 6) ? 1 : 0);
  return spellOnLetter(key.tonic.letter + d, pcNumber(key.tonic) + semis);
}

/** The diatonic pitch-class numbers of a key (harmonic minor adds the leading tone). */
export function diatonicSet(key: Key): Set<number> {
  const set = new Set<number>();
  for (let d = 1; d <= 7; d++) set.add(pcNumber(degreePc(key, d)));
  if (key.mode === 'minor') set.add(pcNumber(degreePc(key, 7, true)));
  return set;
}

/** Position of the key signature on the line of fifths: C/a = 0, G/e = 1, F/d = -1 … */
export function fifthsPosition(key: Key): number {
  const letterFifths = [0, 2, 4, -1, 1, 3, 5]; // C D E F G A B
  const tonic = letterFifths[key.tonic.letter] + 7 * key.tonic.alter;
  return key.mode === 'major' ? tonic : tonic - 3;
}

/** Distance between two keys in steps of the circle of fifths (relative keys are 0 apart). */
export function keyDistance(a: Key, b: Key): number {
  return Math.abs(fifthsPosition(a) - fifthsPosition(b));
}

/**
 * Describe a pitch class as a scale degree of a key: "1", "#4", "b7".
 * Minor keys are read against the natural minor scale, so the leading tone is "#7".
 */
export function degreeToken(pc: PitchClass, key: Key): string {
  const degree = mod(pc.letter - key.tonic.letter, 7) + 1;
  const expected = degreePc(key, degree);
  let diff = mod(pcNumber(pc) - pcNumber(expected), 12);
  if (diff > 6) diff -= 12;
  const acc = diff > 0 ? '#'.repeat(diff) : diff < 0 ? 'b'.repeat(-diff) : '';
  return acc + degree;
}

export function tonicLetterName(key: Key): string {
  return LETTERS[key.tonic.letter];
}

const NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];

/**
 * A key named from another key's point of view, as a Roman numeral: in F major, D minor is "vi"
 * and C major "V"; in A minor, C major is "III". Case follows the mode of `key`; a chromatic
 * relation gets an accidental ("bVI", "#iv"). Minor home keys are read against natural minor.
 */
export function keyRelation(home: Key, key: Key): string {
  const token = degreeToken(key.tonic, home);
  const acc = token.replace(/\d/g, '');
  const numeral = NUMERALS[Number(token.replace(/\D/g, '')) - 1];
  return acc + (key.mode === 'minor' ? numeral.toLowerCase() : numeral);
}
